import {
  AccountContactPreferencesDocument,
  AccountRelationshipType,
  AccountType,
  AccountWithTypeFragment,
  ChildRelationshipFragment,
  IntroductionFragment,
  MinimalAccountRelationshipsDocument,
  MinimalRelationshipAccountFragment,
  ParentOrChildRelationshipFragment,
  ParentRelationshipFragment,
  RelationshipType,
  useDeleteRelationshipMutation,
  useMinimalAccountRelationshipsQuery,
  useSetIntermediaryMutation,
  useSetPocMutation,
} from 'graphql/bindings/AdminBindings'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  ContactInfoRow,
  EllipsisVIcon,
  PencilAltIcon,
  PlusSquareIcon,
  SingleInfoCard,
  TrashIcon,
  Typography,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import { CardWarningNotes } from 'isolated-design-library/components/CardWarningNotes'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { RelationshipsModal } from 'components/shared/RelationshipsModal'
import { Routes } from 'util/Routes'
import { childRelationshipMap, parentRelationshipMap } from 'util/RelationshipUtils'
import { getGravatarUrl } from 'util/GravatarUtil'
import { indefiniteArticled } from 'util/StringUtils'
import React, { Fragment } from 'react'
import capitalize from 'lodash/capitalize'
import concat from 'lodash/concat'
import moment from 'moment'
import remove from 'lodash/remove'

interface RelationshipCoreData {
  id: string
  title?: string | null | undefined
  startDate?: moment.Moment | null | undefined
  relationship?: RelationshipType
  sourceBrokerOrg?: string | null | undefined
  destBrokerOrg?: string | null | undefined
}

const RelationshipInfoCard: React.FC<{
  association: string
  associatedAccount: MinimalRelationshipAccountFragment
  overrideDestAccount?: MinimalRelationshipAccountFragment
  originAccount: AccountWithTypeFragment
  relationship: RelationshipCoreData
  isIntermediary?: boolean
  isPoc?: boolean
  supressHighlight?: boolean
  showPOCInMenu?: boolean
  parenthetical?: string | null | undefined
}> = ({
  association,
  associatedAccount,
  overrideDestAccount,
  supressHighlight,
  showPOCInMenu,
  isPoc,
  originAccount,
  relationship,
  isIntermediary = false,
  parenthetical,
}) => {
  const [setAsIntermediary] = useSetIntermediaryMutation({
    refetchQueries: [
      { query: MinimalAccountRelationshipsDocument, variables: { id: originAccount.id } },
      { query: AccountContactPreferencesDocument, variables: { id: originAccount.id } },
    ],
  })
  const [setPOC] = useSetPocMutation({
    refetchQueries: [
      { query: MinimalAccountRelationshipsDocument, variables: { id: originAccount.id } },
      { query: AccountContactPreferencesDocument, variables: { id: originAccount.id } },
    ],
  })
  const [deleteRelationship] = useDeleteRelationshipMutation({
    variables: { relationshipId: relationship.id },
    refetchQueries: [
      { query: MinimalAccountRelationshipsDocument, variables: { id: originAccount.id } },
      { query: AccountContactPreferencesDocument, variables: { id: originAccount.id } },
    ],
  })
  let destAccount = overrideDestAccount ?? associatedAccount
  const existingRelationship = {
    id: relationship.id,
    account: {
      label: destAccount.name,
      value: {
        id: destAccount.id,
        name: destAccount.name,
        type: destAccount.type,
      },
    },
    start_date: relationship.startDate && moment(relationship.startDate),
    is_intermediary: (destAccount?.intermediary?.id ?? '') === relationship.id || isIntermediary,
    source_parent_org_id: relationship.sourceBrokerOrg,
    dest_parent_org_id: relationship.destBrokerOrg,
    relationship: relationship.relationship,
    title: relationship.title,
    type: destAccount.type,
  }
  const [showModal, hideModal] = useModal(() => {
    return (
      <RelationshipsModal
        hideModal={hideModal}
        accountId={originAccount.id}
        accountName={originAccount.name}
        accountType={originAccount.type}
        editing
        existingRelationship={existingRelationship}
      />
    )
  }, [originAccount, associatedAccount, existingRelationship])
  const isIssuer = associatedAccount.type === 'issuer'
  const phone = associatedAccount.formatted_phone_number || ''
  const email = associatedAccount.email || ''
  const actions = (
    <Fragment>
      <PopoverListItem button onClick={showModal}>
        <PopoverListItemIcon>
          <PencilAltIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Edit</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem
        button
        data-cypress="set-intermediary-button"
        onClick={() =>
          setAsIntermediary({
            variables: {
              form: { accountId: originAccount.id, intermediaryAccountId: isIntermediary ? null : relationship.id },
            },
          })
        }>
        <PopoverListItemIcon>
          <PlusSquareIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>{isIntermediary ? 'Remove as' : 'Set'} intermediary</PopoverListItemText>
      </PopoverListItem>
      {showPOCInMenu && (
        <PopoverListItem
          button
          data-cypress="set-poc-button"
          onClick={() => setPOC({ variables: { relationshipId: relationship.id, isPointOfContact: !isPoc } })}>
          <PopoverListItemIcon>
            <PlusSquareIcon />
          </PopoverListItemIcon>
          <PopoverListItemText>{isPoc ? 'Remove as' : 'Set'} Point of Contact</PopoverListItemText>
        </PopoverListItem>
      )}
      <PopoverListItem button onClick={() => deleteRelationship()}>
        <PopoverListItemIcon>
          <TrashIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Delete</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )
  let notesSection: React.ReactNode
  if (associatedAccount.intermediary) {
    notesSection = <CardWarningNotes text={'Contact through an intermediary.'} />
  } else {
    notesSection = phone || email ? <ContactInfoRow phone={phone} email={email} /> : undefined
  }
  return (
    <SingleInfoCard
      data-cypress={'relationship'}
      logo={associatedAccount?.issuer_account?.company?.logo_url ?? getGravatarUrl(email)}
      leftTitle={
        associatedAccount.name +
        (parenthetical ? ` (${parenthetical})` : '') +
        (isIntermediary && !supressHighlight ? ' (Intermediary)' : '') +
        (isPoc && !supressHighlight && !isIntermediary ? ' (POC)' : '')
      }
      leftSubtitle={association}
      link={isIssuer ? Routes.issuer(associatedAccount.id) : Routes.account(associatedAccount.id)}
      notes={notesSection}
      action={<PopoverAction icon={<EllipsisVIcon />} actionMenu={actions} />}
      highlight={(isIntermediary || isPoc) && !supressHighlight}
    />
  )
}

const ChildAccountCard: React.FC<{
  child: ChildRelationshipFragment
  parentName: string
  parentId: string
  parentType: AccountType
  isIntermediary?: boolean
  supressHighlight?: boolean
}> = ({ child, parentName, parentId, parentType, supressHighlight, isIntermediary = false }) => {
  const relationship = child.parent_to_child_relationship || ''
  let association = `${parentName}'s ${child.title}`
  if (parentName.endsWith('s')) association = `${parentName}' ${child.title}`
  const mappedRelationship = relationship ? childRelationshipMap[relationship] : undefined
  const { id, title, start_date: startDate } = child
  const shouldShowPocMenu =
    parentType !== AccountType.Individual &&
    child.child_account.type === AccountType.Individual &&
    relationship === AccountRelationshipType.Employs
  return (
    <RelationshipInfoCard
      associatedAccount={child.child_account}
      association={association}
      originAccount={{ id: parentId, name: parentName, type: parentType }}
      relationship={{
        id,
        title,
        startDate,
        relationship: mappedRelationship,
      }}
      key={child.child_account.id}
      isIntermediary={isIntermediary}
      supressHighlight={supressHighlight}
      isPoc={child.is_point_of_contact}
      showPOCInMenu={shouldShowPocMenu}
    />
  )
}

const ParentAccountCard: React.FC<{
  parent: ParentRelationshipFragment
  childName: string
  childId: string
  childType: AccountType
  isIntermediary?: boolean
  parentBrokerOrg?: string | null | undefined
  childBrokerOrg?: string | null | undefined
  supressHighlight?: boolean
}> = ({
  parent,
  childName,
  childId,
  childType,
  supressHighlight,
  isIntermediary = false,
  parentBrokerOrg,
  childBrokerOrg,
}) => {
  const relationship = parent.parent_to_child_relationship || ''
  let association =
    relationship === 'affiliated_with' ? `${childName} is affiliated` : `${capitalize(relationship)} ${childName}`
  if (parent.title) {
    association += ' as ' + indefiniteArticled(parent.title || '')
  }
  const mappedRelationship = relationship ? parentRelationshipMap[relationship] : undefined
  const { id, title, start_date: startDate } = parent
  return (
    <RelationshipInfoCard
      associatedAccount={parent.parent_account}
      association={association}
      originAccount={{ id: childId, name: childName, type: childType }}
      relationship={{
        id,
        title,
        startDate,
        relationship: mappedRelationship,
        destBrokerOrg: parentBrokerOrg,
        sourceBrokerOrg: childBrokerOrg,
      }}
      key={parent.parent_account.id}
      isIntermediary={isIntermediary}
      supressHighlight={supressHighlight}
    />
  )
}

const IntroductionCard: React.FC<{
  intro: ParentOrChildRelationshipFragment & IntroductionFragment
  originAccount: AccountWithTypeFragment
}> = ({ intro: { start_date: startDate, introduction, child_account, parent_account, title, id }, originAccount }) => {
  const formattedDate = moment(startDate).format('M/D/YYYY')
  const childBrokerAccount = introduction?.child_broker_account
  const parentBrokerAccount = introduction?.parent_broker_account

  let association = parentBrokerAccount
    ? `On behalf of ${parentBrokerAccount?.name} on ${formattedDate}`
    : `On ${formattedDate}`

  if (parentBrokerAccount?.id === originAccount.id) {
    association = `Via ${parent_account?.name} on ${formattedDate}`
  }

  return (
    <RelationshipInfoCard
      associatedAccount={child_account}
      association={association}
      originAccount={originAccount}
      parenthetical={childBrokerAccount?.name}
      relationship={{
        id,
        title,
        startDate,
        relationship: RelationshipType.WasIntroducedBy,
        sourceBrokerOrg: parentBrokerAccount?.id,
        destBrokerOrg: childBrokerAccount?.id,
      }}
    />
  )
}

const IntroducedByCard: React.FC<{
  intro: ParentOrChildRelationshipFragment & IntroductionFragment
  originAccount: AccountWithTypeFragment
}> = ({ intro: { id, title, start_date: startDate, introduction, parent_account, child_account }, originAccount }) => {
  const formattedDate = moment(startDate).format('M/D/YYYY')
  const childBrokerAccount = introduction?.child_broker_account
  const parentBrokerAccount = introduction?.parent_broker_account

  let association = childBrokerAccount ? `With ${childBrokerAccount?.name} on ${formattedDate}` : `On ${formattedDate}`

  if (childBrokerAccount?.id === originAccount.id) {
    association = `Via ${child_account?.name} on ${formattedDate}`
  }

  return (
    <RelationshipInfoCard
      associatedAccount={parent_account}
      parenthetical={parentBrokerAccount?.name}
      association={association}
      originAccount={originAccount}
      relationship={{
        id,
        title,
        startDate,
        relationship: RelationshipType.Introduced,
        sourceBrokerOrg: childBrokerAccount?.id,
        destBrokerOrg: parentBrokerAccount?.id,
      }}
    />
  )
}

type DirectedRelationship =
  | { type: 'PARENT'; relationship: ParentRelationshipFragment }
  | { type: 'CHILD'; relationship: ChildRelationshipFragment }

const Relationships: React.FC = () => {
  const { match } = useRouter<{ id: string }>()
  const { data } = useMinimalAccountRelationshipsQuery({ variables: { id: match.params.id } })
  const account = data?.account
  const accountId = account?.id ?? ''
  const intermediaryId = account?.intermediary?.id
  const accountName = account?.name ?? ''
  const accountType = account?.type ?? AccountType.Individual
  const accountRelationships = (account?.relationships ?? []).filter(
    r => r.parent_to_child_relationship !== AccountRelationshipType.Introduced
  )
  const introductions = account?.introducedRelationships ?? []
  const introducedBy = account?.introducedByRelationships ?? []
  const accountParents = (account?.parents ?? []).filter(
    r => r.parent_to_child_relationship !== AccountRelationshipType.Introduced
  )
  const children: DirectedRelationship[] = accountRelationships.map(relationship => ({ type: 'CHILD', relationship }))
  const parents: DirectedRelationship[] = accountParents.map(relationship => ({ type: 'PARENT', relationship }))
  let relationships = concat(parents, children)
  if (intermediaryId) {
    const intermediary = remove(relationships, r => r.relationship.id === intermediaryId)
    relationships = concat(intermediary, relationships)
  }

  const [showModal, hideModal] = useModal(() => {
    return (
      <RelationshipsModal
        hideModal={hideModal}
        accountId={accountId}
        accountName={accountName}
        accountType={accountType}
      />
    )
  }, [accountType, accountName, accountId])
  return (
    <Card data-cypress="account-relationships-card" id="relationships-panel">
      <CardHeader title="Relationships" />
      <CardContent>
        {relationships.map(relationship => {
          switch (relationship.type) {
            case 'CHILD':
              let child = relationship.relationship
              return (
                <ChildAccountCard
                  child={child}
                  parentName={accountName}
                  parentType={accountType}
                  parentId={accountId}
                  key={child.child_account.id}
                  isIntermediary={intermediaryId === child.id}
                />
              )
            case 'PARENT':
              let parent = relationship.relationship
              return (
                <ParentAccountCard
                  parent={parent}
                  childName={accountName}
                  childId={accountId}
                  childType={accountType}
                  key={parent.parent_account.id}
                  isIntermediary={intermediaryId === parent.id}
                />
              )
            default:
              return null
          }
        })}
        {introducedBy.length > 0 && (
          <Box mb={1} mt={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Introduced by
            </Typography>
          </Box>
        )}
        {introducedBy.map(intro => (
          <IntroducedByCard
            intro={intro}
            originAccount={{ id: accountId, name: accountName, type: accountType }}
            key={intro.id}
          />
        ))}
        {introductions.length > 0 && (
          <Box mb={1} mt={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Introductions
            </Typography>
          </Box>
        )}
        {introductions.map(intro => (
          <IntroductionCard
            intro={intro}
            originAccount={{ id: accountId, name: accountName, type: accountType }}
            key={intro.id}
          />
        ))}
        <Box mt={2}>
          <Button size="large" fullWidth onClick={showModal} data-cypress="add-relationship-button">
            Add relationship
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export { Relationships, ChildAccountCard, ParentAccountCard }
